/**
 * Whether the browser is running a build that is no longer the deployed one.
 *
 * A bundle cannot know it is stale: it has no idea what has been deployed since it loaded. So it carries its
 * own version, baked in at build time, and asks `version.json` — a file small enough to fetch often and
 * deployed with no-cache so the check itself is never the thing that is out of date.
 *
 * This is not cosmetic. Customers have run cached builds long enough that a fixed bug read as still broken,
 * and support has twice chased a fault that was an old build: the Device Service CORS report, where the
 * advice they were following had already been corrected, and a workflow whose new fields were invisible
 * because the catalog response was cached.
 */

/** Injected by Vite at build time. */
declare const __APP_VERSION__: string;

export const RUNNING_VERSION: string = typeof __APP_VERSION__ === 'string' ? __APP_VERSION__ : 'unknown';

/**
 * The deployed version, or null when it cannot be read.
 *
 * Null is NOT "up to date" and not "stale" — it is no answer, and the caller must leave the banner alone.
 * Offline, a blocked request, or a 404 during a deploy would otherwise nag someone whose build is current.
 */
export async function fetchDeployedVersion(): Promise<string | null> {
  try {
    /*
     * `no-store` and a cache-busting parameter both. The header is what should be enough; the parameter is
     * for the intermediary that ignores it, which — given customers are reaching us through corporate proxies
     * — is the case this exists to survive.
     */
    const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { version?: unknown };
    return typeof body.version === 'string' && body.version ? body.version : null;
  } catch {
    return null;
  }
}

/**
 * Whether a reload would change anything.
 *
 * Only ever true when BOTH versions are known and they differ. An unknown running version (a dev server with
 * no define) never reports an update, because telling a developer to reload on every poll is how a warning
 * gets ignored.
 */
export function isStale(running: string, deployed: string | null): boolean {
  if (!deployed || !running || running === 'unknown') return false;
  return running !== deployed;
}
